/**
 * What a client's phone actually receives, as pure functions with no server
 * dependencies. The server sends through these and the admin Settings →
 * Texting preview renders through these, so the preview cannot drift from
 * the text that goes out.
 *
 * The parts an owner can never edit away live HERE, outside the owner's
 * words: the business name and "Reply STOP to opt out." on a first text, and
 * the sign-up link on the reply to a stranger.
 */

export const STOP_LINE = 'Reply STOP to opt out.';

/** What the client actually receives. The first text names the business and carries the STOP line. */
export function clientTextBody(args: { content: string; firstText: boolean; tenantName: string }): string {
  const content = args.content.trim();
  return args.firstText ? `${args.tenantName}: ${content}\n\n${STOP_LINE}` : content;
}

/**
 * The reply to a number that isn't a client, in the owner's words. It is
 * always that number's first text from us, so it always names the business,
 * carries the way in, and ends with the STOP line.
 */
export function unknownSenderCustomText(args: { content: string; businessName: string; signUpUrl: string }): string {
  return `${args.businessName}: ${args.content.trim()}\n\nSign up: ${args.signUpUrl}\n\n${STOP_LINE}`;
}

// ─── SMS segments ────────────────────────────────────────────────────────

/** GSM-7 basic set: each is one unit. */
const GSM_BASIC =
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';
/** GSM-7 extension set: each costs two units. */
const GSM_EXTENDED = '^{}\\[~]|€\f';

export interface SmsSegments {
  /** Characters as a person counts them. */
  characters: number;
  encoding: 'gsm7' | 'ucs2';
  /** How many texts the carrier bills this as. 0 for an empty message. */
  segments: number;
  /** What fits in one text at this length and encoding (160/153 or 70/67). */
  perSegment: number;
  /** The first few characters that forced the smaller size, for the owner. */
  specialCharacters: string[];
}

/**
 * Segment count the way carriers bill it. One character outside GSM-7 (an em
 * dash, a curly apostrophe, an emoji) switches the WHOLE message to UCS-2,
 * which holds 70 characters a text instead of 160. Past one text, each part
 * loses room to the joining header (153 / 67).
 */
export function smsSegments(text: string): SmsSegments {
  const chars = Array.from(text);
  const special: string[] = [];
  let units = 0;
  for (const ch of chars) {
    if (GSM_BASIC.includes(ch)) units += 1;
    else if (GSM_EXTENDED.includes(ch)) units += 2;
    else if (!special.includes(ch)) special.push(ch);
  }
  if (special.length > 0) {
    // UCS-2 counts UTF-16 code units: an emoji is two.
    const length = text.length;
    const perSegment = length <= 70 ? 70 : 67;
    return {
      characters: chars.length,
      encoding: 'ucs2',
      segments: Math.ceil(length / perSegment),
      perSegment,
      specialCharacters: special.slice(0, 3),
    };
  }
  const perSegment = units <= 160 ? 160 : 153;
  return {
    characters: chars.length,
    encoding: 'gsm7',
    segments: units === 0 ? 0 : Math.ceil(units / perSegment),
    perSegment,
    specialCharacters: [],
  };
}
